import { SavedWeldIndicationGraph } from 'features/reporting/types'
import { useRolodexSignedURLs } from 'features/rolodex'
import { useEffect, useMemo, useState } from 'react'

import { WeldIndication } from './useWeldIndications'

export function useWeldIndicationsWithImages(
  weldIndications: WeldIndication[],
  savedWeldIndicationGraphs:
    | Record<string, SavedWeldIndicationGraph>
    | undefined,
  metadataEntryId: string
) {
  const [renderedImageUrls, setRenderedImageUrls] = useState<
    Record<string, string>
  >({})

  const renderedImageRefs = useMemo(() => {
    const refs: Array<{ path: string; indicationId: string }> = []

    if (!savedWeldIndicationGraphs) return refs

    // Process all saved graphs (both indications and file scans)
    Object.keys(savedWeldIndicationGraphs).forEach((key) => {
      const savedGraph = savedWeldIndicationGraphs[key]
      if (savedGraph?.imagePath) {
        refs.push({
          path: savedGraph.imagePath,
          indicationId: key,
        })
      }
    })

    return refs
  }, [savedWeldIndicationGraphs])

  // Get signed URLs for rendered images
  const { results: renderedImageUrlResults } = useRolodexSignedURLs(
    renderedImageRefs.map((ref) => ({
      entry_id: metadataEntryId,
      path: ref.path,
    }))
  )

  // Process rendered image URLs
  useEffect(() => {
    if (renderedImageRefs.length === 0 || renderedImageUrlResults.length === 0)
      return

    const newUrls: Record<string, string> = {}

    // Match results to indication IDs
    renderedImageUrlResults.forEach((result, index) => {
      if (result.signed_url && renderedImageRefs[index]) {
        newUrls[renderedImageRefs[index].indicationId] = result.signed_url
      }
    })

    setRenderedImageUrls(newUrls)
  }, [renderedImageRefs, renderedImageUrlResults])

  // Filter to only include weld indications that have rendered images
  return {
    weldIndicationsWithImages: weldIndications.filter(
      (indication) => renderedImageUrls[indication.id]
    ),
    renderedWeldImageUrls: renderedImageUrls,
  }
}
